import React, {
  useState, useEffect, useMemo, useRef, useContext
} from 'react';
import PropTypes from 'prop-types';
import {
  params, string, arrayOf, shape, extend
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { Button } from '@one-thd/sui-atomic-components';
import { Add } from '@one-thd/sui-icons';
import { AddToQuote } from '@thd-olt-component-react/add-to-quote';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import {
  ADD_TO_TYPE, SCREENS, TRACK, ITEM_DETAILS_LINK
} from './AddToConstants';
import { AddToProvider } from './AddToContext';
import { SaveToFavoriteIconButton } from './addto/list/SaveToFavoriteIconButton';
import {
  handleOpenATQ,
  getValidSku,
  isItemIntent,
  stopListeningNow,
  triggerAnalytics,
  triggerLoadingDrawerAnalytics,
  updateNonQuoteableProducts
} from './AddToHelpers';
import { useSelectedItems } from './hooks/useSelectedItems';
import { AddToDrawer } from './AddToDrawer';

export const AddTo = (props) => {
  const {
    addToFallbackType,
    addToType: addToTypeFromProp,
    copyProjectData,
    CustomAddToActionElement,
    defaultOpen,
    list,
    loadedProductsData,
    products,
    projectDetails,
    showBackButton,
    showSelectionScreen,
    storeId: storeIdFromProp,
    updateLoadedProductsData,
    productPodItemData,
    screen: screenFromProp,
    simpleAddToQuote,
    onATCClick,
    aiListBuilderData,
    zipCode
  } = props;
  const defaultAddToType = addToTypeFromProp === ADD_TO_TYPE.PROJECT && addToFallbackType !== null
    ? addToFallbackType : addToTypeFromProp;
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const [addToType, setAddToType] = useState(defaultAddToType || null);
  const [initialLoad, setInitialLoad] = useState(true);
  const store = useStore();
  const {
    storeId: projectStoreId, zipCode: projectZipCode, projectId, id, projectName
  } = projectDetails || {};
  const storeId = storeIdFromProp || projectStoreId || store?.storeId;
  const [open, setOpen] = useState(defaultOpen);
  const [screen, setScreen] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedProductDetails, setSelectedProductDetails] = useState(
    { itemId: null, quantity: null, productData: null }
  );
  const projectProductsNeedingSkuDataRef = useRef([]);
  const addToQuoteRef = useRef(null);
  const quoteTimeTrackingRef = useRef({ startTime: null });
  const [atcDrawerType, setAtcDrawerType] = useState('');

  const { customerType } = useThdCustomer() || {};
  const isProjectsEligible = customerType === 'B2B';

  useEffect(() => {
    if (customerType === 'B2B') {
      setAddToType(addToTypeFromProp);
    } else if (addToTypeFromProp === 'Project') {
      setAddToType(addToFallbackType);
    }
  }, [customerType, addToTypeFromProp, addToFallbackType]);

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('add-to.ready');
  }, []);

  const {
    PROJECT_SELECT_GROUP, DEFAULT, MINI_PIP_BASE, PROJECT_SELECT_PROJECT, ADD_TO_SELECTION,
    LOADING_STATUS, PRODUCT_EXCEPTIONS, MINI_PIP_FULL
  } = SCREENS;

  const projectProductsNeedingSkuData = useMemo(() => {
    if (!open || addToType !== ADD_TO_TYPE.PROJECT || !products?.length || products.length <= 1) {
      projectProductsNeedingSkuDataRef.current = [];
      return [];
    }

    if (projectProductsNeedingSkuDataRef.current.length) {
      return projectProductsNeedingSkuDataRef.current;
    }

    const initialProductsNeedingSkuData = products.filter((product) => {
      if (isItemIntent(product)) {
        return false;
      }

      return !getValidSku(product?.sku, product?.identifiers);
    });

    projectProductsNeedingSkuDataRef.current = initialProductsNeedingSkuData;
    return initialProductsNeedingSkuData;
  }, [open, addToType, products]);

  const loadingProgress = useMemo(() => {
    if (![ADD_TO_TYPE.QUOTE, ADD_TO_TYPE.PROJECT].includes(addToType) && !showSelectionScreen) {
      return null;
    }
    const productsWithoutIntents = products?.filter((item) => item?.itemType !== 'intent');
    const totalProducts = productsWithoutIntents?.length || 0;
    if (totalProducts === 0) {
      return 0;
    }
    if (addToType === ADD_TO_TYPE.PROJECT) {
      if (!projectProductsNeedingSkuData.length) {
        return 100;
      };

      const loadedItemIds = new Set(
        loadedProductsData.map((loadedProduct) => loadedProduct?.itemId).filter(Boolean)
      );

      const matchCount = productsWithoutIntents.filter((product) => {
        return loadedItemIds.has(product?.itemId);
      }).length;

      return Math.round((matchCount / totalProducts) * 100);
    }
    const matchCount = productsWithoutIntents.filter((product) => {
      const productItemId = product?.itemId;
      const productId = product?.id;

      return loadedProductsData.some((obj) => (productItemId && obj?.itemId === productItemId)
      || (productId && obj?.key === productId)
      );
    }).length;

    return Math.round((matchCount / totalProducts) * 100);
  }, [
    loadedProductsData,
    products,
    addToType,
    showSelectionScreen,
    projectProductsNeedingSkuData
  ]);

  const isLoading = loadingProgress !== 100;

  const {
    selectedCatalogItems,
    selectedQuotableCatalogItemDetails,
    selectedCatalogItemDetails,
    selectedQCItemDetails,
    selectedNonSharedQCItemDetails
  } = useSelectedItems({ products, loadedProductsData, loadingProgress, showSelectionScreen });

  const handleClose = (__event) => {
    stopListeningNow();

    const currentScreen = screen;
    setInitialLoad(true);
    setScreen(null);
    if (showSelectionScreen) {
      setAddToType(null);
    }
    if (addToType === 'Cart' && !!atcDrawerType) {
      setScreen(MINI_PIP_FULL);
      setAtcDrawerType('');
    }
    setShowErrorMessage(false);
    setShowSuccessMessage(false);
    setSelectedGroup(null);
    setSelectedProductDetails({ itemId: null, quantity: null, productData: null });
    triggerAnalytics(TRACK.ADD_TO_OVERLAY_EXIT, { screen });
    setOpen(false);
    if (currentScreen === LOADING_STATUS && quoteTimeTrackingRef.current.startTime) {
      triggerLoadingDrawerAnalytics('loading interrupted', quoteTimeTrackingRef.current.startTime);
    }
    quoteTimeTrackingRef.current.startTime = null;
  };

  const nonQuoteableProducts = useMemo(() => {
    if (isLoading) {
      return [];
    }

    return updateNonQuoteableProducts(
      selectedCatalogItemDetails,
      selectedQCItemDetails,
      selectedNonSharedQCItemDetails
    );
  }, [loadingProgress, selectedCatalogItemDetails, selectedQCItemDetails, selectedNonSharedQCItemDetails]);

  useEffect(() => {
    if (addToType === ADD_TO_TYPE.QUOTE && open) {
      quoteTimeTrackingRef.current.startTime = Math.floor(Date.now() / 1000);
    }
  }, [addToType, open]);

  useEffect(() => {
    if (!open || addToType !== ADD_TO_TYPE.QUOTE) {
      return;
    }

    if (simpleAddToQuote) {
      handleOpenATQ(addToQuoteRef);
      handleClose();
      return;
    }

    if (isLoading) {
      if (screen !== LOADING_STATUS) {
        setScreen(LOADING_STATUS);
        window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-quote.loadingScreen');
      }
      return;
    }

    triggerLoadingDrawerAnalytics('loading finished', quoteTimeTrackingRef.current.startTime);

    if (nonQuoteableProducts?.length > 0 && loadingProgress === 100) {
      setScreen(PRODUCT_EXCEPTIONS);
    } else {
      handleOpenATQ(addToQuoteRef);
      handleClose();
    }
  }, [open, loadingProgress, addToType, nonQuoteableProducts]);

  useEffect(() => {
    if (!open || addToType !== ADD_TO_TYPE.PROJECT) {
      return;
    }

    const hasPendingProjectProductFetch = projectProductsNeedingSkuData.length > 0 && loadingProgress !== 100;

    if (hasPendingProjectProductFetch) {
      if (screen !== LOADING_STATUS) {
        setScreen(LOADING_STATUS);
      }
      return;
    }

    if (screen === LOADING_STATUS) {
      setScreen(PROJECT_SELECT_PROJECT);
    }
  }, [open, addToType, loadingProgress, screen, projectProductsNeedingSkuData.length]);

  const setInitialScreen = () => {
    if (addToType === 'Project') {
      if (screenFromProp === MINI_PIP_BASE) {
        setScreen(MINI_PIP_BASE);
        triggerAnalytics(ITEM_DETAILS_LINK, {});
      } else if (products?.length > 0) {
        setScreen(PROJECT_SELECT_PROJECT);
      } else {
        setScreen(DEFAULT);
      }
    }
    if (addToType === 'Cart') {
      if (screenFromProp === MINI_PIP_FULL) {
        setScreen(MINI_PIP_FULL);
      } else {
        setScreen(DEFAULT);
      }
    }
    if (addToType === 'DisplayItemDetails' && products?.length > 0) {
      setScreen(SCREENS.MINI_PIP_BASE);
    }
  };

  const triggerOpenEvents = () => {
    if (addToType === 'Project' && !screenFromProp) {
      if (products?.length === 0) {
        window.LIFE_CYCLE_EVENT_BUS.trigger('projects: add items overlay');
      }
    }
    if (window.digitalData?.page?.pageInfo?.pageType === 'pip') {
      window.LIFE_CYCLE_EVENT_BUS.trigger(TRACK.BUTTON_CLICK, {});
    }
    if (addToType === 'List') {
      if (products?.length > 0) {
        LIFE_CYCLE_EVENT_BUS.trigger(TRACK.OPEN_DRAWER, {});
      }
      if (products?.length === 0) {
        const type = list?.isMaterialsButton ? 'add materials button' : 'start shopping button';
        window.LIFE_CYCLE_EVENT_BUS.trigger('add-to.addToMaterials', {
          buttonType: type
        });
      }
    }
    if (addToType === 'Cart' && window.digitalData?.page?.pageInfo?.pageType === 'maml:buy it again') {
      window.LIFE_CYCLE_EVENT_BUS.trigger('buy-again.quickView');
    }
  };

  const handleOpen = () => {
    if (isMobile) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('add-to.mobile');
    }
    if (showSelectionScreen) {
      setScreen(ADD_TO_SELECTION);
      window.LIFE_CYCLE_EVENT_BUS.trigger('add-to.addTo');
    }
    setInitialScreen();
    setOpen(true);
    triggerOpenEvents();
  };

  const handleBackPress = () => {
    if (screen === PROJECT_SELECT_GROUP || screen === MINI_PIP_BASE) {
      setScreen(DEFAULT);
      setSelectedGroup(null);
      setSelectedProductDetails({ itemId: null, quantity: null, productData: null });
    }
    if (screen.startsWith('MINI_PIP')) {
      setScreen(MINI_PIP_BASE);
    }
  };

  useEffect(() => {
    if (defaultOpen) {
      setOpen(defaultOpen);
    }
  }, [defaultOpen]);

  const context = {
    projectRefetch: () => { },
    handleBackPress,
    handleClose,
    screen,
    setScreen,
    selectedProductDetails,
    setSelectedProductDetails,
    showSuccessMessage,
    setShowSuccessMessage,
    showErrorMessage,
    setShowErrorMessage,
    addToType,
    products,
    selectedGroup,
    setSelectedGroup,
    storeId,
    defaultOpen,
    atcDrawerType,
    setAtcDrawerType,
    onATCClick,
    productPodItemData,
    zipCode
  };

  if (addToType === null && !showSelectionScreen) {
    return null;
  }
  const showCustomButton = CustomAddToActionElement && (addToType === addToTypeFromProp || showSelectionScreen);
  const { showIconButton = false } = list;
  return (
    <>
      {
        (showSelectionScreen && !showCustomButton) && (
          <Button
            startIcon={Add}
            variant="tertiary"
            onClick={handleOpen}
            disabled={!products?.length > 0 && !copyProjectData?.items?.length > 0}
          >
            Add to
          </Button>
        )
      }
      {
        showIconButton && (
          <SaveToFavoriteIconButton
            storeId={storeId}
            products={products}
            handleOpen={handleOpen}
          />
        )
      }
      {
        showCustomButton && (
          <CustomAddToActionElement onClick={handleOpen} />
        )
      }
      {
        !showCustomButton && !showIconButton && !showSelectionScreen && (
          <Button
            fullWidth
            variant="secondary"
            onClick={handleOpen}
            aria-label={`Add to ${addToType}`}
            data-testid={`add-to-${addToType}`}
          >
            Add to {addToType === 'CopyProject' ? 'Project' : addToType}
          </Button>
        )
      }
      <AddToProvider value={context}>
        <AddToDrawer
          open={open}
          setOpen={setOpen}
          showBackButton={showBackButton}
          nonQuoteableProducts={nonQuoteableProducts}
          loadedProductsData={loadedProductsData}
          setAddToType={setAddToType}
          addToQuoteRef={addToQuoteRef}
          projectDetails={projectDetails}
          loadingProgress={loadingProgress}
          selectedCatalogItems={selectedCatalogItems}
          list={list}
          initialLoad={initialLoad}
          setInitialLoad={setInitialLoad}
          copyProjectData={copyProjectData}
          isProjectsEligible={isProjectsEligible}
          updateLoadedProductsData={updateLoadedProductsData}
          selectedQuotableCatalogItemDetails={selectedQuotableCatalogItemDetails}
          productPodItemData={productPodItemData}
          aiListBuilderData={aiListBuilderData}
          isDisplayOnly={screenFromProp === MINI_PIP_BASE}
        />
      </AddToProvider>
      {(addToType === ADD_TO_TYPE.QUOTE || showSelectionScreen) && (
        <div className="sui-hidden">
          {(!simpleAddToQuote ? (
            <AddToQuote
              buttonRef={addToQuoteRef}
              products={selectedQuotableCatalogItemDetails}
              productExceptions={nonQuoteableProducts}
              projectQuoteData={{
                projectZipCode,
                projectName,
                projectId
              }}
              quoteStartTime={quoteTimeTrackingRef.current.startTime}
            />
          ) : (
            <AddToQuote
              buttonRef={addToQuoteRef}
              itemId={products[0]?.itemId}
              quantity={products[0]?.quantity || 1}
            />
          ))}
        </div>
      )}
    </>
  );
};

AddTo.displayName = 'AddTo';

AddTo.propTypes = {
  addToType: PropTypes.oneOf(
    [ADD_TO_TYPE.PROJECT, ADD_TO_TYPE.LIST, ADD_TO_TYPE.QUOTE, ADD_TO_TYPE.CART, ADD_TO_TYPE.COPY_PROJECT, ADD_TO_TYPE.DISPLAY_ITEM_DETAILS, null]
  ),
  addToFallbackType: PropTypes.oneOf(
    [ADD_TO_TYPE.PROJECT, ADD_TO_TYPE.LIST, ADD_TO_TYPE.QUOTE, ADD_TO_TYPE.CART, null]
  ),
  products: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({
      itemId: PropTypes.string,
      quantity: PropTypes.number,
      itemType: PropTypes.string,
      id: PropTypes.string,
      sku: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          storeSkuNumber: PropTypes.string,
          specialOrderSku: PropTypes.string,
          omsThdSku: PropTypes.string
        })
      ]),
      intent: PropTypes.string,
      groupName: PropTypes.string,
      aiQuantityEstimate: PropTypes.number,
      bridgeSolution: PropTypes.shape({
        optionKey: PropTypes.string,
        qcId: PropTypes.string
      })
    })),
    PropTypes.arrayOf(PropTypes.shape({
      itemId: PropTypes.string,
      quantity: PropTypes.number,
      intent: PropTypes.string,
      groupName: PropTypes.string,
      aiQuantityEstimate: PropTypes.number
    }))
  ]),
  copyProjectData: PropTypes.shape({
    sourceList: PropTypes.string,
    items: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})])
    ),
    targetProjectId: PropTypes.string
  }),
  storeId: PropTypes.string,
  CustomAddToActionElement: PropTypes.func,
  showSelectionScreen: PropTypes.bool,
  list: PropTypes.shape({
    showCount: PropTypes.bool,
    isSharedList: PropTypes.bool,
    showIconButton: PropTypes.bool,
    skipListId: PropTypes.string,
    listId: PropTypes.string,
    onMaterialsAdded: PropTypes.func,
    isMaterialsButton: PropTypes.bool
  }),
  aiListBuilderData: PropTypes.shape({
    onNewIntentsSave: PropTypes.func,
    refType: PropTypes.string,
    refId: PropTypes.string,
    title: PropTypes.string,
    materials: PropTypes.arrayOf(PropTypes.shape({
      groupName: PropTypes.string,
      products: PropTypes.arrayOf(PropTypes.shape({
        quantity: PropTypes.number,
        product: PropTypes.string,
      }))
    }))
  }),
  showBackButton: PropTypes.bool,
  projectDetails: PropTypes.shape({
    id: PropTypes.string,
    projectId: PropTypes.string,
    projectName: PropTypes.string,
    storeId: PropTypes.string,
    zipCode: PropTypes.string
  }),
  loadedProductsData: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    key: PropTypes.string,
    identifiers: PropTypes.shape({
      brandName: PropTypes.string,
      productLabel: PropTypes.string,
      storeSkuNumber: PropTypes.string,
      productType: PropTypes.string,
    }),
    pricing: PropTypes.shape({
      value: PropTypes.number,
    }),
    fulfillment: PropTypes.shape({}),
    info: PropTypes.shape({
      productType: PropTypes.string,
      isLiveGoodsProduct: PropTypes.bool,
    }),
    media: PropTypes.shape({
      images: PropTypes.arrayOf(PropTypes.shape({
        url: PropTypes.string,
        type: PropTypes.string,
        subType: PropTypes.string,
      })),
    }),
    product: PropTypes.shape({}),
    price: PropTypes.shape({}),
  })),
  updateLoadedProductsData: PropTypes.func,
  defaultOpen: PropTypes.bool,
  productPodItemData: PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    searchKeyword: PropTypes.string,
    onIntentResolved: PropTypes.func,
  }),
  screen: PropTypes.string,
  simpleAddToQuote: PropTypes.bool,
  onATCClick: PropTypes.func,
  zipCode: PropTypes.string
};

AddTo.defaultProps = {
  list: {
    skipListId: null,
    showIconButton: false,
    showCount: false,
    isSharedList: false,
    listId: null,
    onMaterialsAdded: () => { },
    isMaterialsButton: false
  },
  aiListBuilderData: {
    onNewIntentsSave: null,
    refType: 'LIST',
    refId: ''
  },
  products: [],
  copyProjectData: {},
  storeId: null,
  CustomAddToActionElement: null,
  showSelectionScreen: false,
  addToType: null,
  addToFallbackType: null,
  showBackButton: true,
  projectDetails: {},
  loadedProductsData: [],
  updateLoadedProductsData: () => {},
  defaultOpen: false,
  productPodItemData: {},
  screen: null,
  simpleAddToQuote: false,
  onATCClick: null,
  zipCode: undefined
};

AddTo.dataModel = extend({
  product: params({
    itemId: string().isRequired(),
    dataSource: string()
  }).shape({
    itemId: string(),
    dataSources: string(),
    identifiers: shape({
      brandName: string(),
      productLabel: string(),
      storeSkuNumber: string()
    }),
    media: shape({
      images: arrayOf(shape({
        url: string(),
        type: string(),
        subType: string()
      }))
    }),
    reviews: shape({
      ratingsReviews: shape({
        averageRating: string(),
        totalReviews: string()
      })
    })
  })
},
AddToQuote,
SaveToFavoriteIconButton,
AddToDrawer
);
